import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Users, Route as RouteIcon, CalendarClock, Percent, ShieldAlert, ShieldCheck, ShieldX, Clock, Radio, AlertTriangle, BadgeCheck } from "lucide-react";
import { KpiCard } from "@/components/dashboards/KpiCard";
import { TrendChart } from "@/components/dashboards/TrendChart";
import { DashboardFilters, defaultFilters, type DashboardFilterState } from "@/components/dashboards/DashboardFilters";
import { DashboardCustomizer } from "@/components/dashboards/DashboardCustomizer";
import { useKpiOverview, useTrend } from "@/lib/dashboards";
import { useDashboardLayout } from "@/lib/dashboardLayouts";
import { GridDashboard } from "@/components/dashboards/GridDashboard";
import { MyPostOrders } from "@/components/MyPostOrders";

export const Route = createFileRoute("/_authenticated/dashboards/")({
  component: ExecutiveDashboard,
  head: () => ({ meta: [{ title: "Executive overview — MissionReady" }] }),
});

function ExecutiveDashboard() {
  const [filters, setFilters] = useState<DashboardFilterState>(defaultFilters());
  const range = { siteId: filters.siteId, fromDate: filters.fromDate, toDate: filters.toDate };
  const kpi = useKpiOverview(range);
  const incidents = useTrend("incidents", range);
  const dispatch = useTrend("dispatch", range);
  const overtime = useTrend("overtime", range);
  const compliance = useTrend("compliance", range);
  const k = kpi.data;
  const { widgets } = useDashboardLayout("executive");

  const renderers: Record<string, () => React.ReactNode> = {
    "kpi.activeOfficers":     () => <KpiCard label="Active officers" value={k?.activeOfficers} icon={Users} tone="success" loading={kpi.isLoading} />,
    "kpi.activePatrols":      () => <KpiCard label="Active guard tours" value={k?.activePatrols} icon={RouteIcon} loading={kpi.isLoading} />,
    "kpi.openShifts":         () => <KpiCard label="Open shifts" value={k?.openShifts} icon={CalendarClock} loading={kpi.isLoading} />,
    "kpi.fulfillment":        () => <KpiCard label="Fulfillment" value={k?.fulfillmentPct} suffix="%" icon={Percent} tone="primary" loading={kpi.isLoading} />,
    "kpi.complianceWarnings": () => <KpiCard label="Compliance warnings" value={k?.complianceWarnings} icon={ShieldAlert} tone="warning" loading={kpi.isLoading} />,
    "kpi.complianceBlocks":   () => <KpiCard label="Compliance blocks" value={k?.complianceBlocks} icon={ShieldX} tone="danger" loading={kpi.isLoading} />,
    "kpi.expiring7d":         () => <KpiCard label="Expiring 7d" value={k?.expiring7d} icon={BadgeCheck} tone="warning" loading={kpi.isLoading} />,
    "kpi.expiring30d":        () => <KpiCard label="Expiring 30d" value={k?.expiring30d} icon={ShieldCheck} loading={kpi.isLoading} />,
    "kpi.dispatchThroughput": () => <KpiCard label="Dispatch throughput" value={k?.dispatchThroughput} icon={Radio} loading={kpi.isLoading} />,
    "kpi.openIncidents":      () => <KpiCard label="Open incidents" value={k?.openIncidents} icon={AlertTriangle} tone="warning" loading={kpi.isLoading} />,
    "kpi.overtime":           () => <KpiCard label="Overtime" value={k?.overtimeHours} suffix="h" icon={Clock} loading={kpi.isLoading} />,
    "chart.incidents":        () => <TrendChart title="Incident trend" points={incidents.data?.points} loading={incidents.isLoading} />,
    "chart.dispatch":         () => <TrendChart title="Dispatch throughput" points={dispatch.data?.points} loading={dispatch.isLoading} color="hsl(var(--success))" />,
    "chart.overtime":         () => <TrendChart title="Overtime (minutes/day)" points={overtime.data?.points} valueKey="total" loading={overtime.isLoading} color="hsl(var(--warning))" />,
    "chart.compliance":       () => <TrendChart title="Compliance issues" points={compliance.data?.points} loading={compliance.isLoading} color="hsl(var(--destructive))" />,
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <DashboardFilters value={filters} onChange={setFilters} />
        <DashboardCustomizer dashboardKey="executive" />
      </div>
      <GridDashboard
        dashboardKey="executive"
        widgets={widgets}
        render={(id: string) => renderers[id]?.() ?? null}
      />
      <MyPostOrders />

    </div>
  );
}
